import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, ChevronDown, ChevronRight, ExternalLink, Eye, Loader2, Plus, RotateCcw, X,
} from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { KbImportRunsPanel } from "@/components/KbImportRunsPanel";

const SOURCE_LABEL = {
  intercom: "Intercom article",
  intercom_snippet: "Intercom snippet",
  helpjuice: "Helpjuice",
  helpjuice_import: "Helpjuice",
};

/** Article body HTML → the visible text, one line per block, for diffing. */
function htmlToLines(html) {
  let s = String(html || "");
  s = s.replace(/<\/(p|div|li|h[1-6]|tr|blockquote|pre|table)>/gi, "\n");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<[^>]+>/g, " ");
  let txt = s;
  try {
    txt = new DOMParser().parseFromString(s, "text/html").body.textContent || s;
  } catch { /* keep s */ }
  return txt.split("\n").map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean).slice(0, 800);
}

/** Longest-common-subsequence line diff → [{t:'same'|'add'|'del', text}]. */
function diffLines(a, b) {
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ t: "same", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "del", text: a[i] }); i++; }
    else { out.push({ t: "add", text: b[j] }); j++; }
  }
  while (i < n) out.push({ t: "del", text: a[i++] });
  while (j < m) out.push({ t: "add", text: b[j++] });
  return out;
}

function InlineDiff({ current, incoming }) {
  const rows = diffLines(htmlToLines(current), htmlToLines(incoming));
  const added = rows.filter((r) => r.t === "add").length;
  const removed = rows.filter((r) => r.t === "del").length;
  if (!added && !removed) {
    return <p className="text-[11px] text-slate-500">No text differences — the change is in formatting or metadata.</p>;
  }
  return (
    <div data-testid="kb-review-diff" className="max-h-80 overflow-auto rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-2 font-mono text-[11px] leading-relaxed">
      <div className="mb-1 text-slate-400">+{added} added · −{removed} removed</div>
      {rows.map((r, k) => (
        <div key={k} className={
          r.t === "add" ? "bg-emerald-100/70 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
            : r.t === "del" ? "bg-rose-100/70 text-rose-800 line-through dark:bg-rose-900/30 dark:text-rose-300"
            : "text-slate-500 dark:text-slate-400"}>
          <span className="select-none opacity-50">{r.t === "add" ? "+ " : r.t === "del" ? "− " : "  "}</span>
          {r.text}
        </div>
      ))}
    </div>
  );
}


function ReviewRow({ item, onDone }) {
  const isNew = item.kind === "new";
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(isNew);
  const [busy, setBusy] = useState("");

  const expand = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try {
        const { data } = await api.get(`/kb/import/changes/${item.article_id}`);
        setDetail(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the change");
        setOpen(false);
      } finally { setLoading(false); }
    }
  };

  const apply = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post(`/kb/import/changes/${item.article_id}/apply`);
      toast.success(isNew
        ? `Added to the KB${data.slug ? ` as /kb/a/${data.slug}` : ""}`
        : "Update applied — the previous version is saved in History");
      onDone(item.article_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (isNew ? "Couldn't add the article" : "Couldn't apply the update"));
    } finally { setBusy(""); }
  };

  const dismiss = async () => {
    setBusy("dismiss");
    try {
      await api.post(`/kb/import/changes/${item.article_id}/dismiss`);
      toast.success(isNew ? "Declined — it won't be offered again (see Declined below to reconsider)" : "Dismissed — your copy is kept");
      onDone(item.article_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't dismiss it");
    } finally { setBusy(""); }
  };

  const titleChanged = !isNew && item.new_title && item.title && item.new_title !== item.title;

  return (
    <Card className="p-0 overflow-hidden" data-testid={`kb-review-item-${item.article_id}`}>
      <button type="button" onClick={expand}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900/40"
        data-testid={`kb-review-expand-${item.article_id}`}>
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
          isNew ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}
          data-testid={`kb-review-kind-${item.article_id}`}>
          {isNew ? "New" : "Changed"}
        </span>
        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {SOURCE_LABEL[item.source] || item.source}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
            {item.title || item.new_title}
            {item.is_snippet && <span className="ml-1.5 rounded bg-indigo-50 px-1 text-[10px] font-semibold text-indigo-600">Snippet</span>}
          </span>
          {titleChanged && (
            <span className="block truncate text-[11px] text-amber-700 dark:text-amber-400">
              new title: {item.new_title}
            </span>
          )}
          {isNew && (
            <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400" data-testid={`kb-review-meta-${item.article_id}`}>
              {item.category ? `Category: ${item.category}` : "Uncategorised"}
              {item.new_word_count ? ` · ${item.new_word_count} words` : ""}
              {item.is_draft ? " · draft at the source" : ""}
            </span>
          )}
        </span>
        <span className="shrink-0 text-[11px] text-slate-400">
          {String(item.detected_at || "").slice(0, 10)}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-3">
          {loading || !detail ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {isNew ? (
                <div className="mb-2 space-y-0.5 text-[11px] text-slate-500 dark:text-slate-400" data-testid={`kb-review-new-facts-${item.article_id}`}>
                  {detail.incoming?.description && <div>{detail.incoming.description}</div>}
                  <div>
                    Visible to: {(detail.incoming?.audiences || []).join(", ") || "internal"}
                    {detail.incoming?.new_categories?.length
                      ? ` · creates categor${detail.incoming.new_categories.length === 1 ? "y" : "ies"}: ${detail.incoming.new_categories.join(" › ")}`
                      : ""}
                    {detail.incoming?.keywords?.length ? ` · keywords: ${detail.incoming.keywords.slice(0, 6).join(", ")}` : ""}
                  </div>
                </div>
              ) : detail.current?.name !== detail.incoming?.name && (
                <div className="mb-2 text-[11px]">
                  <span className="text-slate-400">Title: </span>
                  <span className="text-rose-700 line-through dark:text-rose-300">{detail.current?.name}</span>
                  <span className="text-slate-400"> → </span>
                  <span className="text-emerald-700 dark:text-emerald-300">{detail.incoming?.name}</span>
                </div>
              )}
              {preview || isNew ? (
                <div data-testid="kb-review-preview"
                  className="prose prose-sm max-h-80 max-w-none overflow-auto rounded-md border border-slate-200 dark:border-slate-800 p-3 dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detail.incoming?.body_html || "") }} />
              ) : (
                <InlineDiff current={detail.current?.body_html} incoming={detail.incoming?.body_html} />
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={apply} disabled={!!busy}
                  data-testid={`kb-review-apply-${item.article_id}`}>
                  {busy === "apply" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : isNew ? <Plus className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                  {isNew ? "Include in KB" : "Apply update"}
                </Button>
                <Button size="sm" variant="outline" onClick={dismiss} disabled={!!busy}
                  data-testid={`kb-review-dismiss-${item.article_id}`}>
                  {busy === "dismiss" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    : <X className="mr-1 h-3.5 w-3.5" />}
                  {isNew ? "Don't include" : "Dismiss"}
                </Button>
                {!isNew && (
                  <Button size="sm" variant="ghost" onClick={() => setPreview((p) => !p)}
                    data-testid={`kb-review-preview-toggle-${item.article_id}`}>
                    <Eye className="mr-1 h-3.5 w-3.5" /> {preview ? "Show diff" : "Preview incoming"}
                  </Button>
                )}
                {isNew && item.source_url && (
                  <a href={item.source_url} target="_blank" rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                    data-testid={`kb-review-source-link-${item.article_id}`}>
                    <ExternalLink className="h-3 w-3" /> Open at the source
                  </a>
                )}
                {!isNew && item.slug && (
                  <Link to={`/kb/a/${item.slug}`} target="_blank"
                    className="ml-auto text-[11px] text-primary hover:underline">
                    Open current article
                  </Link>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Section({ title, hint, items, onDone, testid, action }) {
  return (
    <section className="mt-5" data-testid={testid}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title} <span className="text-slate-400">({items.length})</span></h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
        </div>
        {action}
      </div>
      <div className="space-y-2">
        {items.map((it) => <ReviewRow key={it.article_id} item={it} onDone={onDone} />)}
      </div>
    </section>
  );
}

/** Articles a manager chose not to include — a click puts them back on offer. */
function DeclinedPanel({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/kb/import/declined").then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  }, [refreshKey]);
  if (!rows.length) return null;
  const reconsider = async (r) => {
    setBusy(r.article_id);
    try {
      await api.post(`/kb/import/declined/${r.article_id}/reconsider`);
      setRows((p) => p.filter((x) => x.article_id !== r.article_id));
      toast.success("It will be offered again on the next import run");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't reconsider it");
    } finally { setBusy(""); }
  };
  return (
    <section className="mt-6" data-testid="kb-review-declined">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900"
        data-testid="kb-review-declined-toggle">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Declined articles ({rows.length}) — not offered again unless you reconsider
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          {rows.map((r) => (
            <li key={r.article_id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs" data-testid={`kb-review-declined-${r.article_id}`}>
              <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{SOURCE_LABEL[r.source] || r.source}</span>
              <span className="min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-200">{r.name}</span>
              <span className="text-[11px] text-slate-400">{r.declined_by}{r.declined_at ? ` · ${String(r.declined_at).slice(0, 10)}` : ""}</span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={busy === r.article_id}
                onClick={() => reconsider(r)} data-testid={`kb-review-reconsider-${r.article_id}`}>
                <RotateCcw className="mr-1 h-3 w-3" /> Reconsider
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function KBImportReview() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [declinedKey, setDeclinedKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/import/changes");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the review queue");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeOne = (aid) => { setItems((prev) => (prev || []).filter((x) => x.article_id !== aid)); setDeclinedKey((k) => k + 1); };
  const fresh = (items || []).filter((i) => i.kind === "new");
  const changed = (items || []).filter((i) => i.kind !== "new");

  const applyAll = async (kind) => {
    const n = kind === "new" ? fresh.length : changed.length;
    const msg = kind === "new"
      ? `Include all ${n} new article${n === 1 ? "" : "s"} in the KB? Each keeps the visibility it had at the source.`
      : `Apply all ${n} pending update${n === 1 ? "" : "s"}? Each article's current version is saved in History first.`;
    if (!window.confirm(msg)) return;
    setBusy(kind);
    try {
      const { data } = await api.post("/kb/import/changes/apply-all", { kind });
      toast.success(kind === "new"
        ? `${data.applied} article${data.applied === 1 ? "" : "s"} added${data.failed ? `, ${data.failed} skipped` : ""}`
        : `${data.applied} update${data.applied === 1 ? "" : "s"} applied${data.failed ? `, ${data.failed} failed` : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't apply all");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="kb-import-review-page">
      <PageHeader compact title="Import review"
        subtitle="Nothing from Intercom or Helpjuice lands in the KB on its own. New source articles wait here until you include them; articles that changed at their source show a diff to apply or dismiss. Your copies are never overwritten." />

      {items === null ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500" data-testid="kb-review-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="mt-6 p-8 text-center" data-testid="kb-review-empty">
          <Check className="mx-auto h-7 w-7 text-emerald-500" />
          <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">Nothing to review</p>
          <p className="mt-1 text-xs text-slate-500">Every imported article is in sync with its source. New source articles and changes will show up here after the next import.</p>
        </Card>
      ) : (
        <>
          {fresh.length > 0 && (
            <Section testid="kb-review-new-section" title="New at the source" items={fresh} onDone={removeOne}
              hint="Articles we have never held. Open one to read it, then include it or leave it out."
              action={(
                <Button size="sm" variant="outline" onClick={() => applyAll("new")} disabled={!!busy} data-testid="kb-review-include-all">
                  {busy === "new" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
                  Include all {fresh.length}
                </Button>
              )} />
          )}
          {changed.length > 0 && (
            <Section testid="kb-review-changed-section" title="Changed at the source" items={changed} onDone={removeOne}
              hint="Already in the KB; the words changed at the source since we imported them. Your copy is untouched until you apply."
              action={(
                <Button size="sm" onClick={() => applyAll("changed")} disabled={!!busy} data-testid="kb-review-apply-all">
                  {busy === "changed" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}
                  Apply all {changed.length}
                </Button>
              )} />
          )}
        </>
      )}
      <DeclinedPanel refreshKey={declinedKey} />
      <KbImportRunsPanel />
    </div>
  );
}
